/**
 * Owner: Masa Bayu
 * File: marketplace-locale.ts
 * Date:Time: 2026-10-19 18:30
 * Description: Locale marketplace — default English + USD, auto-tukar ikut
 *   negara (ms atau region MY → Bahasa Melayu + MYR). Semua label paparan UI
 *   mengikut bahasa; simbol mata wang mengikut negara. §418 — pemilih bahasa
 *   dunia: `interfaceTag` = tag ISO 639-1 DIPILIH (memandu Intl/format),
 *   `uiTranslation` = gerbang jujur katalog label (certified ↔ fallback).
 */

import { EN_LABELS, MS_LABELS, priceBandTable, type MarketplaceLabels } from './marketplace-labels';
import { findWorldLanguage } from './world-languages';

export type MarketplaceLanguage = 'en' | 'ms';
export type MarketplaceCurrency = 'USD' | 'MYR';
/** §418 — 'certified' = katalog UI wujud; 'fallback' = label kekal English. */
export type MarketplaceUiTranslation = 'certified' | 'fallback';

/** Bentuk sempit yang dipakai semua pengguna label (sah untuk literal `{language, currency}`). */
export interface MarketplaceLocale {
  readonly language: MarketplaceLanguage;
  readonly currency: MarketplaceCurrency;
}

export interface MarketplaceLocaleProfile extends MarketplaceLocale {
  /** Tag ISO 639-1 bahasa yang dipilih pengguna (untuk Intl, arah tulisan, skrip). */
  readonly interfaceTag: string;
  readonly uiTranslation: MarketplaceUiTranslation;
}

export type { MarketplaceLabels } from './marketplace-labels';

/** §466 S1 — satu-satunya pembentuk jumlah (koma ribuan wajib; aturan Owner). */
export function formatMarketplaceAmount(priceMinorUnits: number): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(priceMinorUnits / 100);
}

export function formatMarketplacePrice(
  priceMinorUnits: number,
  currency: MarketplaceCurrency,
): string {
  const amount = formatMarketplaceAmount(priceMinorUnits);
  if (currency === 'MYR') {
    return `RM ${amount}`;
  }
  return `$${amount}`;
}

/** §466 S1 — harga null bermakna pelayan belum menghantar harga: paparkan status,
 *  jangan 0.00 (harga percuma) yang merupakan klaim palsu. Mata wang yang tidak
 *  dikenali juga gagal-keras ke label yang sama (tiada simbol rekaan). */
function asMarketplaceCurrency(currency: string | null): MarketplaceCurrency | null {
  return currency === 'MYR' || currency === 'USD' ? currency : null;
}

export function formatMarketplacePriceOrUnknown(
  priceMinorUnits: number | null,
  currency: string | null,
  unknownLabel: string,
): string {
  const code = asMarketplaceCurrency(currency);
  return priceMinorUnits === null || code === null
    ? unknownLabel
    : formatMarketplacePrice(priceMinorUnits, code);
}

export function formatCartAriaLabel(count: number, language: MarketplaceLanguage): string {
  if (language === 'ms') {
    return `Troli, ${String(count)} item`;
  }
  return count === 1 ? `Cart, 1 item` : `Cart, ${String(count)} items`;
}

export function formatShowing(
  from: number,
  to: number,
  total: number,
  language: MarketplaceLanguage,
): string {
  if (language === 'ms') {
    return `Menunjukkan ${String(from)} – ${String(to)} daripada ${String(total)} buku`;
  }
  return `Showing ${String(from)} – ${String(to)} of ${String(total)} books`;
}

export const MARKETPLACE_LABELS: Readonly<Record<MarketplaceLanguage, MarketplaceLabels>> =
  Object.freeze({
    en: EN_LABELS,
    ms: MS_LABELS,
  });

/**
 * Resolve profil locale daripada bahasa pelayar (BCP-47).
 * Bahasa hanya "ms" apabila subtag bahasa ialah "ms"; mata wang MYR apabila
 * negara MY atau bahasa ms. Selain itu: English + USD (default QX).
 */
export function resolveMarketplaceLocaleProfile(navLanguage: string): MarketplaceLocale {
  const tag = navLanguage.trim().toLowerCase();
  const parts = tag.split(/[-_]/);
  const languageSubtag = parts[0] ?? '';
  const regionSubtag = parts[1] ?? '';
  const language: MarketplaceLanguage = languageSubtag === 'ms' ? 'ms' : 'en';
  const currency: MarketplaceCurrency = regionSubtag === 'my' || language === 'ms' ? 'MYR' : 'USD';
  return { language, currency };
}

export function resolveMarketplaceLabels(profile: MarketplaceLocale): MarketplaceLabels {
  const base = MARKETPLACE_LABELS[profile.language];
  if (profile.currency === 'MYR' && profile.language === 'en') {
    return Object.freeze({
      ...base,
      priceBands: priceBandTable('MYR', 'Free'),
    });
  }
  if (profile.currency === 'USD' && profile.language === 'ms') {
    return Object.freeze({
      ...base,
      priceBands: priceBandTable('USD', 'Percuma'),
    });
  }
  return base;
}

/**
 * §466 S1 — nama bahasa buku dibaca dari `languageRef` manifest hub, bukan dari
 * jadual rekaan. Katalog UI hanya punya 'en'/'ms'; bahasa lain memakai nama
 * English registri dunia §418; tag yang langsung tidak dikenal dipaparkan mentah
 * (tekaan nama bahasa lebih buruk daripada kod mentah).
 */
export function languageDisplayLabel(ref: string, uiLanguage: MarketplaceLanguage): string {
  const names = MARKETPLACE_LABELS[uiLanguage].detail.languageNames;
  if (ref === 'en' || ref === 'ms') {
    return names[ref];
  }
  return findWorldLanguage(ref)?.englishName ?? ref;
}

/**
 * §417/§418 — pilihan bahasa manual di Profil Reader. Override disimpan di
 * localStorage dan boleh jadi SEBARANG tag ISO 639-1 yang sah; mata wang
 * TETAP ikut negara (bahasa ≠ negara: Owner boleh English + MYR).
 * 'auto' / nilai kosong / tidak sah = kembali kepada deteksi §381.
 */
const LANGUAGE_OVERRIDE_KEY = 'qxread.locale.language';

/** Gerbang katalog UI: hanya bahasa di bawah ini punya terjemahan label sebenar. */
export function isUiCatalogCertified(tag: string): tag is MarketplaceLanguage {
  return Object.prototype.hasOwnProperty.call(MARKETPLACE_LABELS, tag);
}

/** Tag selamat untuk Intl API: guna tag pilihan; pelayar menolak → 'en-US'. */
export function safeIntlTag(interfaceTag: string): string {
  try {
    new Intl.DateTimeFormat(interfaceTag);
    return interfaceTag;
  } catch {
    return 'en-US';
  }
}

export function setMarketplaceLanguageOverride(language: string): void {
  const entry = findWorldLanguage(language);
  try {
    if (entry === null) {
      localStorage.removeItem(LANGUAGE_OVERRIDE_KEY);
    } else {
      localStorage.setItem(LANGUAGE_OVERRIDE_KEY, entry.tag);
    }
  } catch {
    /* localStorage disabled (private mode): pilihan kekal semasa sesi UI sahaja */
  }
}

export function getMarketplaceLanguageOverride(): string | null {
  try {
    return findWorldLanguage(localStorage.getItem(LANGUAGE_OVERRIDE_KEY))?.tag ?? null;
  } catch {
    return null;
  }
}

/** Profil hasil deteksi §381 sahaja (tanpa override) — untuk pilihan "Auto". */
export function getDetectedLocaleProfile(): MarketplaceLocale {
  const navLanguage =
    typeof navigator === 'undefined' || typeof navigator.language !== 'string'
      ? 'en-US'
      : navigator.language;
  return detectMarketplaceProfile({
    language: navLanguage,
    languages: Array.isArray(navigator?.languages) ? navigator.languages : [],
    timeZone: readIanaTimeZone(),
    icuRegion: readIcuRegion(),
  });
}

export function getMarketplaceLocaleProfile(): MarketplaceLocaleProfile {
  const detected = getDetectedLocaleProfile();
  const interfaceTag = getMarketplaceLanguageOverride() ?? detected.language;
  const certified = isUiCatalogCertified(interfaceTag);
  return Object.freeze({
    language: certified ? interfaceTag : 'en',
    currency: detected.currency,
    interfaceTag,
    uiTranslation: certified ? 'certified' : 'fallback',
  });
}

/**
 * Isyarat negara tambahan selain `navigator.language`: sesetengah pelayar
 * melaporkan "en-US"/"en" tanpa subtag MY walaupun pengguna di Malaysia —
 * zon waktu (Asia/Kuala_Lumpur, Asia/Kuching) dan wilayah ICU menampannya.
 */
export function detectMarketplaceProfile(input: {
  readonly language: string;
  readonly languages?: readonly string[];
  readonly timeZone?: string;
  readonly icuRegion?: string;
}): MarketplaceLocale {
  const base = resolveMarketplaceLocaleProfile(input.language);
  if (base.currency === 'MYR') {
    return base;
  }
  const mySignals = [
    (input.icuRegion ?? '').toUpperCase() === 'MY',
    input.timeZone === 'Asia/Kuala_Lumpur' || input.timeZone === 'Asia/Kuching',
    (input.languages ?? []).some(
      (tag) => /[-_]my$/i.test(tag.trim()) || /^ms([-_]|$)/i.test(tag.trim()),
    ),
  ].some(Boolean);
  if (!mySignals) {
    return base;
  }
  return { language: base.language, currency: 'MYR' };
}

function readIanaTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

function readIcuRegion(): string {
  try {
    return (
      (
        Intl.NumberFormat().resolvedOptions() as Intl.ResolvedNumberFormatOptions & {
          region?: string;
        }
      ).region ?? ''
    );
  } catch {
    return '';
  }
}
